import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../hooks/useAppDispatch';
import { LoginPage } from '../pages/Login';
import { hasCentralAdminClaims } from '../utils/adminAccess';
import { CENTRAL_SIGN_IN_PATH } from '../utils/sessionRecovery';

/**
 * `/login` is NOT a user surface.
 *
 * It used to render the admin "Paste your admin JWT token" form, and it was
 * where every unauthenticated wrong turn ended: `/`, unknown paths, Stripe's
 * `/billing/success|cancel` returns and the settings Back button all fell
 * through the catch-all to it. Anyone who still links here (old bookmarks,
 * other apps) meant "sign in", so this hands them to Central's canonical
 * sign-in page, keeping the `continue` target they were given. `AuthLogin`
 * sanitises `continue` itself, so it is passed through rather than
 * re-validated here.
 */
export function LegacyLoginRedirect() {
  const { search } = useLocation();
  const continueTarget = new URLSearchParams(search).get('continue');
  const target = continueTarget
    ? `${CENTRAL_SIGN_IN_PATH}?continue=${encodeURIComponent(continueTarget)}`
    : CENTRAL_SIGN_IN_PATH;
  return <Navigate to={target} replace />;
}

/**
 * `/admin/login` — the explicit, admin-only entry to the JWT-paste console
 * login. Nothing user-facing links here. A holder of a token that already
 * carries admin claims goes straight to the console at `/`.
 */
export function AdminLoginRoute() {
  const { isAuthenticated, token } = useAppSelector((state) => state.auth);
  if (isAuthenticated && hasCentralAdminClaims(token)) {
    return <Navigate to="/" replace />;
  }
  return <LoginPage />;
}
